/**
 * Rasmni kesish matematikasi — DOM va canvas'siz, shuning uchun testlanadi.
 *
 * Model: kadr (frame) qo'zg'almaydi, rasm uning ostida suriladi va
 * kattalashadi. Uchta son hamma narsani belgilaydi:
 *
 *  - `zoom` — 1 bo'lganda rasm kadrni TO'LDIRADI (kamida bir tomoni kadrga
 *    teng, ikkinchisi undan katta). 1 dan kichik zoom — butun rasm kadrga
 *    sig'adi va atrofida bo'sh joy qoladi: uzun amortizator kabi tovarni
 *    kesib tashlamasdan kvadratga joylash uchun.
 *  - `offset` — rasm markazining kadr markazidan siljishi (ekran pikselida).
 *  - kadr o'lchami — ekrandagi piksel; natija rasm ASL piksellaridan olinadi.
 */

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Rect extends Point, Size {}

/** Kesish nisbatlari (kenglik / balandlik). Kartochkada rasm kvadrat. */
export const CROP_ASPECTS = {
  square: 1,
  landscape: 4 / 3,
  portrait: 3 / 4,
  /** Banner: server `1200x620` ga keltiradi, shuning uchun nisbat aynan shu. */
  banner: 1200 / 620,
} as const;

export type CropAspect = keyof typeof CROP_ASPECTS;

/** Tovar rasmi uchun ro'yxat (banner nisbati unda yo'q). */
export const CROP_ASPECT_KEYS: readonly CropAspect[] = ['square', 'landscape', 'portrait'];

export const MAX_ZOOM = 4;

/**
 * Natijaning uzun tomoni. Server eng kattasini 1200 px qiladi; 1600 —
 * zaxira. Undan katta bo'lsa fayl hajmi 12 MB chegarasiga yaqinlashadi.
 */
export const OUTPUT_MAX_SIDE = 1600;

/** Ekrandagi kadr: uzun tomoni `longSide`, nisbati tanlanganga teng. */
export function frameSize(aspect: CropAspect, longSide: number): Size {
  const ratio = CROP_ASPECTS[aspect];
  return ratio >= 1
    ? { width: longSide, height: roundPixel(longSide / ratio) }
    : { width: roundPixel(longSide * ratio), height: longSide };
}

/**
 * Piksel yaxlitlash. `Math.round` loyihada PUL uchun taqiqlangan (BigInt
 * kerak); bu yerda summa yo'q, ekran va canvas pikseli — ataylab bitta joyda.
 */
function roundPixel(value: number): number {
  // eslint-disable-next-line no-restricted-properties -- piksel, pul emas
  return Math.round(value);
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** Rasm kadrni to'liq qoplaydigan masshtab (zoom = 1). */
export function coverScale(image: Size, frame: Size): number {
  return Math.max(frame.width / image.width, frame.height / image.height);
}

/** Butun rasm kadrga sig'adigan masshtab. */
function containScale(image: Size, frame: Size): number {
  return Math.min(frame.width / image.width, frame.height / image.height);
}

/** Eng kichik zoom: butun rasm kadrga sig'adi. Hech qachon 1 dan katta emas. */
export function minZoom(image: Size, frame: Size): number {
  return containScale(image, frame) / coverScale(image, frame);
}

export function clampZoom(zoom: number, image: Size, frame: Size): number {
  return clamp(zoom, minZoom(image, frame), MAX_ZOOM);
}

/**
 * Siljish chegarasi. Rasm kadrni qoplasa kadr rasmdan CHIQMAYDI; qoplamasa
 * (zoom < 1) shu o'q bo'yicha rasm markazda turadi.
 */
export function clampOffset(offset: Point, image: Size, frame: Size, zoom: number): Point {
  const scale = coverScale(image, frame) * zoom;
  const limitX = Math.max(0, (image.width * scale - frame.width) / 2);
  const limitY = Math.max(0, (image.height * scale - frame.height) / 2);
  return { x: clamp(offset.x, -limitX, limitX), y: clamp(offset.y, -limitY, limitY) };
}

export interface CropPlan {
  /** Asl rasmdan olinadigan bo'lak (rasm chegarasi ichida). */
  source: Rect;
  /** Canvas'da shu bo'lak tushadigan joy. Zoom < 1 bo'lsa chetlarda bo'sh joy qoladi. */
  dest: Rect;
  /** Canvas o'lchami. */
  output: Size;
}

/** Ekrandagi holatdan `drawImage` uchun aniq to'rtburchaklar. */
export function cropPlan(
  image: Size,
  frame: Size,
  zoom: number,
  offset: Point,
  maxSide: number = OUTPUT_MAX_SIDE,
): CropPlan {
  const scale = coverScale(image, frame) * zoom;

  // Kadrning chap-yuqori burchagi asl rasmning qaysi pikseliga to'g'ri keladi.
  const frameX = ((image.width * scale - frame.width) / 2 - offset.x) / scale;
  const frameY = ((image.height * scale - frame.height) / 2 - offset.y) / scale;
  const frameW = frame.width / scale;
  const frameH = frame.height / scale;

  // Hech qachon KATTALASHTIRILMAYDI: manba piksellaridan ko'p berilmaydi.
  const shrink = Math.min(1, maxSide / Math.max(frameW, frameH));
  const output = {
    width: Math.max(1, roundPixel(frameW * shrink)),
    height: Math.max(1, roundPixel(frameH * shrink)),
  };

  const left = Math.max(0, frameX);
  const top = Math.max(0, frameY);
  const right = Math.min(image.width, frameX + frameW);
  const bottom = Math.min(image.height, frameY + frameH);

  const source = {
    x: left,
    y: top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
  const kx = output.width / frameW;
  const ky = output.height / frameH;

  return {
    source,
    dest: {
      x: (left - frameX) * kx,
      y: (top - frameY) * ky,
      width: source.width * kx,
      height: source.height * ky,
    },
    output,
  };
}

/** JPEG manbasi JPEG bo'lib qoladi; PNG/WebP/AVIF shaffofligini saqlash uchun PNG. */
export function outputType(sourceType: string): 'image/jpeg' | 'image/png' {
  return sourceType === 'image/jpeg' ? 'image/jpeg' : 'image/png';
}

export function croppedFileName(original: string, type: 'image/jpeg' | 'image/png'): string {
  const dot = original.lastIndexOf('.');
  const base = dot > 0 ? original.slice(0, dot) : original;
  return `${base}-crop.${type === 'image/jpeg' ? 'jpg' : 'png'}`;
}
